import { Send } from "lucide-react";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { LIMITS, type CommentView } from "../../../../shared/contracts.js";
import { caseCommentEndpoints, useAddComment, type CommentEndpoints } from "../../../api/caseMutations.js";
import { isClientApiError } from "../../../api/http.js";
import { Button } from "../../../ui/Button.js";
import { Dialog, DialogActions } from "../../../ui/Dialog.js";
import { FormField } from "../../../ui/FormField.js";

/**
 * The "Add comment" sheet (spec §11: the one primary action for non-Parts
 * roles; spec §13: 1–2000 characters, author and time recorded by the
 * server).
 *
 * The composer validates length locally so an empty or over-long comment
 * never leaves the device, sends the trimmed text, and treats the server as
 * authoritative for everything else: a 422 lands on the field, any other
 * failure is an alert, and nothing is appended until the server answers with
 * the comment it stored. While the request runs the dialog cannot be
 * dismissed, so a slow network never leaves the person unsure whether the
 * comment went through.
 *
 * The same composer serves a wholesale order (wholesale spec §3.4): handed
 * that order's `endpoints` it posts to the wholesale route and writes the
 * stored comment into the order's cached detail instead of a case's.
 */

/** The server's limit for a comment body (`LIMITS.note`, 1–2000 characters after trimming). */
export const COMMENT_MAX_LENGTH: number = LIMITS.note.max;

export const COMMENT_EMPTY_MESSAGE = "Enter a comment.";
export const COMMENT_TOO_LONG_MESSAGE = `Comments can be at most ${COMMENT_MAX_LENGTH.toLocaleString("en-US")} characters.`;

/** Local check mirroring the server rule: 1–2000 characters after trimming. */
export function validateCommentBody(body: string): string | null {
  const trimmed = body.trim();
  if (trimmed.length === 0) return COMMENT_EMPTY_MESSAGE;
  if (trimmed.length > COMMENT_MAX_LENGTH) return COMMENT_TOO_LONG_MESSAGE;
  return null;
}

/** "1,968 characters left" / "1 character over". */
export function remainingLabel(body: string): string {
  const remaining = COMMENT_MAX_LENGTH - body.trim().length;
  const count = Math.abs(remaining).toLocaleString("en-US");
  const noun = Math.abs(remaining) === 1 ? "character" : "characters";
  return remaining >= 0 ? `${count} ${noun} left` : `${count} ${noun} over`;
}

/** Plain-language copy for a comment that could not be saved. */
export function commentErrorMessage(error: unknown): string {
  if (isClientApiError(error)) {
    if (error.status === 0) {
      return "We can't reach the server right now. Nothing was saved. Check your connection and try again.";
    }
    if (error.status === 401) {
      return "Your session ended before the comment was saved. Sign in again to add it.";
    }
    if (error.status === 403) {
      return "Your account can't add comments right now. Ask an administrator if that seems wrong.";
    }
    if (error.status === 404) {
      return "This case can't be found any more, so the comment was not saved.";
    }
    if (error.status >= 500) {
      return "The server couldn't save the comment. Nothing was saved. Try again in a moment.";
    }
    return error.message;
  }
  return "Something went wrong. Nothing was saved. Try again.";
}

export interface CommentComposerProps {
  open: boolean;
  onClose: () => void;
  caseId: string;
  caseNumber: string;
  /** Where the comment is posted and cached; defaults to the case routes for `caseId`. */
  endpoints?: CommentEndpoints;
  /** Called with the server's comment after it is stored and cached. */
  onAdded: (comment: CommentView) => void;
}

export function CommentComposer({
  open,
  onClose,
  caseId,
  caseNumber,
  endpoints,
  onAdded,
}: CommentComposerProps) {
  if (!open) return null;
  return (
    <CommentComposerDialog
      onClose={onClose}
      caseId={caseId}
      caseNumber={caseNumber}
      {...(endpoints !== undefined ? { endpoints } : {})}
      onAdded={onAdded}
    />
  );
}

/** Mounted only while open, so every opening starts from a blank field. */
function CommentComposerDialog({
  onClose,
  caseId,
  caseNumber,
  endpoints,
  onAdded,
}: Omit<CommentComposerProps, "open">) {
  const fieldId = useId();
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const [body, setBody] = useState("");
  const [clientError, setClientError] = useState<string | null>(null);
  const [api] = useState(() => endpoints ?? caseCommentEndpoints(caseId));
  const mutation = useAddComment(api);
  const pending = mutation.isPending;

  // A fresh case means a fresh composer: nothing typed for one case may be
  // posted against another.
  useEffect(() => {
    setBody("");
    setClientError(null);
  }, [caseId]);

  const serverFieldError =
    isClientApiError(mutation.error) && mutation.error.status === 422
      ? mutation.error.fieldErrors["body"]?.[0] ?? mutation.error.message
      : null;
  const fieldError = clientError ?? serverFieldError;
  const generalError =
    mutation.isError && serverFieldError === null
      ? commentErrorMessage(mutation.error)
      : null;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pending) return;
    const problem = validateCommentBody(body);
    if (problem) {
      setClientError(problem);
      fieldRef.current?.focus();
      return;
    }
    setClientError(null);
    mutation.mutate(
      { body: body.trim() },
      {
        onSuccess: (comment) => {
          onAdded(comment);
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Add comment"
      description={`Everyone who opens ${caseNumber} will see it, with your name and the time. Comments can't be edited or removed.`}
      pending={pending}
      initialFocusRef={fieldRef}
      size="medium"
    >
      <form className="comment-composer" onSubmit={submit} noValidate>
        <FormField
          id={fieldId}
          label="Comment"
          hint={`Up to ${COMMENT_MAX_LENGTH.toLocaleString("en-US")} characters. ${remainingLabel(body)}`}
          error={fieldError}
        >
          {(control) => (
            <textarea
              {...control}
              ref={fieldRef}
              name="body"
              className="text-input textarea-input comment-composer__field"
              data-size="touch"
              rows={5}
              value={body}
              readOnly={pending}
              onChange={(event) => {
                setBody(event.target.value);
                if (clientError) setClientError(null);
                if (mutation.isError) mutation.reset();
              }}
              autoComplete="off"
              spellCheck
            />
          )}
        </FormField>
        {generalError ? (
          <p role="alert" className="form-alert">
            {generalError}
          </p>
        ) : null}
        <DialogActions>
          <Button variant="secondary" disabled={pending} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" icon={<Send />} busy={pending}>
            Post comment
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
